import videojs from "video.js";

interface ResolutionSource {
  src: string;
  type?: string;
  label: string; // e.g. '720p', 'Source', 'auto'
  res: number | string; // numeric like 720, or 0 for auto, or string labels
  // YouTube only
  _yt?: string;
}

interface GroupedSources {
  label: Record<string, ResolutionSource[]>;
  res: Record<string | number, ResolutionSource[]>;
  type: Record<string, ResolutionSource[]>;
}

export interface VideoJsResolutionSwitcherOptions {
  default?: string | number;
  dynamicLabel?: boolean;
  customSourcePicker?: (player: any, sources: ResolutionSource[], label: string) => any;
}

const defaults: VideoJsResolutionSwitcherOptions = {};

let currentResolution: { label: string; sources: ResolutionSource[] } = { label: '', sources: [] };
const menuItemsHolder: Record<string, any> = {};

function setSourcesSanitized(player: any, sources: ResolutionSource[], label: string, customSourcePicker?: VideoJsResolutionSwitcherOptions['customSourcePicker']) {
  currentResolution = { label, sources };
  if (typeof customSourcePicker === 'function') {
    return customSourcePicker(player, sources, label);
  }
  player.src(sources.map((s) => ({ src: s.src, type: s.type, res: s.res })));
  return player;
}

// ---- Resolution menu item ----
const VjsMenuItem = videojs.getComponent('MenuItem') as any;
class ResolutionMenuItem extends VjsMenuItem {
  private readonly onClickListener: (item: any) => void;
  private readonly labelEl?: HTMLElement;
  private readonly srcList: ResolutionSource[];

  constructor(player: any, options: any, onClickListener: (item: any) => void, labelEl?: HTMLElement) {
    super(player, options);
    this.onClickListener = onClickListener;
    this.labelEl = labelEl;
    this.srcList = options.src as ResolutionSource[];

    this.on('click', this.onClick);
    this.on('touchstart', this.onClick);

    if (options.initialySelected) {
      this.showAsLabel();
      this.selected(true);
      this.addClass('vjs-selected');
    }
  }

  private showAsLabel() {
    if (this.labelEl) {
      this.labelEl.innerHTML = this.options_.label;
    }
  }

  // video.js binds this for us
  onClick(customSourcePicker?: VideoJsResolutionSwitcherOptions['customSourcePicker']) {
    this.onClickListener(this);

    const currentTime = this.player_.currentTime();
    const isPaused = this.player_.paused();
    const playbackRate = this.player_.playbackRate();
    const posterImageSrc = this.player_.options_?.poster;

    this.showAsLabel();
    // Also update any visible label element inside the control bar as a hard guarantee
    try {
      const controlBarEl = this.player_?.controlBar?.el?.();
      const labelNode = controlBarEl?.querySelector?.('.vjs-resolution-button-label') as HTMLElement | null;
      if (labelNode) {
        labelNode.innerHTML = this.options_.label;
      }
    } catch (_) { /* no-op */ }
    // Mark this item selected for accessibility/state and styling
    this.selected(true);
    this.addClass('vjs-selected');

    if (!isPaused) {
      this.player_.bigPlayButton?.hide?.();
      if (posterImageSrc && this.player_.posterImage?.setSrc) {
        this.player_.posterImage.setSrc('');
      }
    } else {
      this.player_.bigPlayButton?.show?.();
    }

    if (typeof customSourcePicker !== 'function' && typeof this.options_.customSourcePicker === 'function') {
      customSourcePicker = this.options_.customSourcePicker;
    }

    let handleSeekEvent = 'loadeddata';
    if (this.player_.techName_ !== 'Youtube' && this.player_.preload?.() === 'none' && this.player_.techName_ !== 'Flash') {
      handleSeekEvent = 'timeupdate';
    }

    this.player_.pause();
    const resBtn = this.player_.el()?.querySelector?.('.vjs-resolution-button');
    resBtn?.classList.remove('vjs-menu-open');

    setSourcesSanitized(this.player_, this.srcList, this.options_.label, customSourcePicker).one(handleSeekEvent, function () {
      this.player_.currentTime(currentTime);
      if (!isPaused) {
        this.player_.playbackRate(playbackRate);
        this.player_.play();
        if (posterImageSrc && this.player_.posterImage?.setSrc) {
          this.player_.posterImage.setSrc(posterImageSrc);
        }
      }
      this.player_.trigger('resolutionchange');
      this.player_.hasStarted(true);
    });
  }
}

// ---- Resolution menu button ----
const VjsMenuButton = videojs.getComponent('MenuButton') as any;
class ResolutionMenuButton extends VjsMenuButton {
  private readonly sources: GroupedSources;
  private readonly dynamicLabel: boolean;
  private readonly labelEl: HTMLElement;
  private documentClickHandler?: (e: Event) => void;

  constructor(player: any, options: any, settings: VideoJsResolutionSwitcherOptions, labelEl: HTMLElement) {
    super(player, options, settings);
    this.sources = options.sources as GroupedSources;
    this.dynamicLabel = !!settings.dynamicLabel;
    this.labelEl = labelEl;

    this.labelEl.innerHTML = options.initialySelectedLabel;
    this.controlText('Quality');

    // Ensure the control is visible (original JS toggled; here we explicitly unhide)
    this.el()?.classList.remove('vjs-hidden');

    if (this.dynamicLabel) {
      this.el()?.appendChild(this.labelEl);
    } else {
      const staticLabel = document.createElement('span');
      videojs.dom.appendContent(staticLabel, options.initialySelectedLabel);
      videojs.dom.addClass(staticLabel, 'vjs-resolution-button-staticlabel');
      this.el()?.appendChild(staticLabel);
    }

    // Toggle menu open state on click
    this.el()?.addEventListener('click', (e: Event) => {
      e.stopPropagation();
      this.el()?.classList.toggle('vjs-menu-open');
    });

    // Store reference to the document click handler for proper cleanup
    this.documentClickHandler = (e: Event) => {
      const target = e.target as HTMLElement;
      const el = this.el();
      if (el && !el.contains(target)) {
        el.classList.remove('vjs-menu-open');
      }
    };
    document.addEventListener('click', this.documentClickHandler);
  }

  dispose() {
    // Clean up document event listener
    if (this.documentClickHandler) {
      document.removeEventListener('click', this.documentClickHandler);
      this.documentClickHandler = undefined;
    }
    // Call parent dispose method
    super.dispose?.();
  }

  createItems() {
    const menuItems: any[] = [];
    const srcGroups = (this.options_ && (this.options_ as any).sources) || this.sources || {};
    let labels = (srcGroups && srcGroups.label) || {};
    // Synthesize labels from res groups if missing
    if ((!labels || Object.keys(labels).length === 0) && srcGroups && srcGroups.res) {
      const synthesized: Record<string, ResolutionSource[]> = {};
      Object.keys(srcGroups.res).forEach((resKey) => {
        const list = srcGroups.res[resKey] as ResolutionSource[];
        if (Array.isArray(list) && list.length) {
          const item = list[0];
          synthesized[item.label] = list;
        }
      });
      labels = synthesized;
    }
    const onClickUnselectOthers = (clickedItem: any) => {
      menuItems.forEach((item) => {
        item.selected(item === clickedItem);
        item.removeClass('vjs-selected');
      });
    };
    // Build menu items

    for (const key in labels) {
      if (Object.prototype.hasOwnProperty.call(labels, key)) {
        const item = new ResolutionMenuItem(
          this.player_,
          {
            label: key,
            src: labels[key],
            initialySelected: key === this.options_.initialySelectedLabel,
            customSourcePicker: this.options_.customSourcePicker
          },
          onClickUnselectOthers,
          this.labelEl
        );
        menuItems.push(item);
        menuItemsHolder[key] = item;
      }
    }
    return menuItems;
  }
}

function compareResolutions(a: ResolutionSource, b: ResolutionSource) {
  if (!a.res || !b.res) return 0;
  return (+b.res) - (+a.res);
}

function bucketSources(src: ResolutionSource[]): GroupedSources {
  const resolutions: GroupedSources = { label: {}, res: {}, type: {} };
  src.forEach((source) => {
    if (!resolutions.label[source.label]) resolutions.label[source.label] = [];
    if (!resolutions.res[source.res]) resolutions.res[source.res] = [] as any;
    if (!resolutions.type[source.type || '']) resolutions.type[source.type || ''] = [];

    resolutions.label[source.label].push(source);
    (resolutions.res as any)[source.res].push(source);
    resolutions.type[source.type || ''].push(source);
  });
  return resolutions;
}

function chooseSrc(settings: VideoJsResolutionSwitcherOptions, groupedSrc: GroupedSources, sorted: ResolutionSource[]) {
  let selectedRes: any = settings['default'];
  let selectedLabel = '';

  if (selectedRes === 'second-high' && sorted.length > 1) {
    selectedRes = sorted[1].res;
    selectedLabel = sorted[1].label;
  } else if (selectedRes === 'high') {
    selectedRes = sorted[0].res;
    selectedLabel = sorted[0].label;
  } else if (selectedRes === 'low' || selectedRes == null || !groupedSrc.res[selectedRes]) {
    selectedRes = sorted[sorted.length - 1].res;
    selectedLabel = sorted[sorted.length - 1].label;
  } else if (groupedSrc.res[selectedRes]) {
    selectedLabel = groupedSrc.res[selectedRes][0].label;
  }

  return { res: selectedRes, label: selectedLabel, sources: groupedSrc.res[selectedRes] } as {
    res: any; label: string; sources: ResolutionSource[];
  };
}

function initResolutionForYt(player: any, settings: VideoJsResolutionSwitcherOptions, label: HTMLElement) {
  player.tech_?.ytPlayer?.setPlaybackQuality?.('default');

  player.tech_?.ytPlayer?.addEventListener?.('onPlaybackQualityChange', function () {
    player.trigger('resolutionchange');
  });

  player.one('play', function () {
    const qualities = player.tech_?.ytPlayer?.getAvailableQualityLevels?.() || [];
    const map: Record<string, ResolutionSource> = {
      highres: { res: 1080, label: '1080', _yt: 'highres', src: player.src().src, type: player.src().type },
      hd1080: { res: 1080, label: '1080', _yt: 'hd1080', src: player.src().src, type: player.src().type },
      hd720: { res: 720, label: '720', _yt: 'hd720', src: player.src().src, type: player.src().type },
      large: { res: 480, label: '480', _yt: 'large', src: player.src().src, type: player.src().type },
      medium: { res: 360, label: '360', _yt: 'medium', src: player.src().src, type: player.src().type },
      small: { res: 240, label: '240', _yt: 'small', src: player.src().src, type: player.src().type },
      tiny: { res: 144, label: '144', _yt: 'tiny', src: player.src().src, type: player.src().type },
      auto: { res: 0, label: 'auto', _yt: 'default', src: player.src().src, type: player.src().type }
    };

    const sources: ResolutionSource[] = [];
    qualities.forEach((q: string) => {
      if (map[q]) sources.push({ ...map[q] });
    });

    const grouped = bucketSources(sources);
    const customSourcePicker = function (_player: any, _sources: ResolutionSource[], _label: string) {
      player.tech_?.ytPlayer?.setPlaybackQuality?.(_sources[0]._yt);
      return player;
    };

    const chosen = { label: 'auto', res: 0, sources: grouped.label.auto } as any;
    const menuButton = new ResolutionMenuButton(player, {
      sources: grouped,
      initialySelectedLabel: chosen.label,
      initialySelectedRes: chosen.res,
      customSourcePicker
    }, settings, label);

    menuButton.el().classList.add('vjs-resolution-button');
    player.controlBar.resolutionSwitcher = player.controlBar.addChild('ResolutionSwitcher', {});
  });
}

export function videoJsResolutionSwitcher(this: any, options?: VideoJsResolutionSwitcherOptions) {
  const settings: VideoJsResolutionSwitcherOptions = (videojs.mergeOptions ? videojs.mergeOptions(defaults, options) : { ...defaults, ...(options || {}) });
  const player: any = this;
  const label: HTMLElement = document.createElement('span');
  videojs.dom.addClass(label, 'vjs-resolution-button-label');
  this.el().appendChild(label);

  // Extend player API
  let groupedSrc: GroupedSources = { label: {}, res: {}, type: {} };

  player.updateSrc = function (src?: ResolutionSource[] | ResolutionSource) {
    if (!src) return player.src();

    if (player.controlBar.resolutionSwitcher) {
      player.controlBar.resolutionSwitcher.dispose?.();
      delete player.controlBar.resolutionSwitcher;
    }

    const srcArray: ResolutionSource[] = Array.isArray(src) ? src.slice() : [src];
    srcArray.sort(compareResolutions);
    groupedSrc = bucketSources(srcArray);
    // Synthesize labels if missing
    if (Object.keys(groupedSrc.label).length === 0 && Object.keys(groupedSrc.res).length > 0) {
      const synthesized: Record<string, ResolutionSource[]> = {};
      Object.keys(groupedSrc.res).forEach((resKey) => {
        const list = groupedSrc.res[resKey] as ResolutionSource[];
        if (Array.isArray(list) && list.length) {
          const first = list[0];
          synthesized[first.label || `${resKey}p`] = list;
        }
      });
      groupedSrc.label = synthesized;
    }
    const chosen = chooseSrc(settings, groupedSrc, srcArray);

    // Register container component if not already registered
    const Component = videojs.getComponent('Component');
    class ResolutionSwitcher extends Component {
      constructor(player: any, options?: any) {
        super(player, options);
      }
    }
    videojs.registerComponent('ResolutionSwitcher', ResolutionSwitcher);

    const menuButton = new ResolutionMenuButton(player, {
      sources: groupedSrc,
      initialySelectedLabel: chosen.label,
      initialySelectedRes: chosen.res,
      customSourcePicker: settings.customSourcePicker
    }, settings, label);

    videojs.dom.addClass(menuButton.el(), 'vjs-resolution-button');

    // Add the actual menu button as a child, so its lifecycle and menu are managed by video.js
    const addedButton = player.controlBar.addChild(menuButton, {});
    const buttonEl = addedButton.el();
    const barEl = player.controlBar.el();
    // Place resolution switcher after the download button if present; otherwise before PiP, else append
    const downloadEl = barEl.querySelector('.vjs-vjsdownload');
    if (downloadEl && downloadEl.parentNode === barEl) {
      const afterDownload = (downloadEl as any).nextSibling;
      if (afterDownload) {
        barEl.insertBefore(buttonEl, afterDownload);
      } else {
        barEl.appendChild(buttonEl);
      }
    } else {
      const pip = player.controlBar.getChild?.('PictureInPictureToggle');
      if (pip && (pip as any).el) {
        barEl.insertBefore(buttonEl, (pip as any).el());
      } else {
        barEl.appendChild(buttonEl);
      }
    }
    player.controlBar.resolutionSwitcher = addedButton;

    return setSourcesSanitized(player, chosen.sources, chosen.label);
  };

  player.currentResolution = function (labelName?: string, customSourcePicker?: VideoJsResolutionSwitcherOptions['customSourcePicker']) {
    if (labelName == null) return currentResolution;
    if (menuItemsHolder[labelName]) {
      menuItemsHolder[labelName].onClick(customSourcePicker);
    }
    return player;
  };

  player.getGroupedSrc = function () {
    return groupedSrc;
  };

  player.ready(() => {
    // Always render the control. If at least one source exists, initialize with it; otherwise render empty control.
    const initialSources = player.options_?.sources as ResolutionSource[] | undefined;
    if (initialSources && initialSources.length >= 1) {
      player.updateSrc(initialSources);
    } else if (!player.controlBar.resolutionSwitcher) {
      const Component = videojs.getComponent('Component');
      class ResolutionSwitcher extends Component { }
      if (!videojs.getComponent('ResolutionSwitcher')) {
        videojs.registerComponent('ResolutionSwitcher', ResolutionSwitcher);
      }

      const emptyGrouped: GroupedSources = { label: {}, res: {}, type: {} };
      const menuButton = new ResolutionMenuButton(player, {
        sources: emptyGrouped,
        initialySelectedLabel: 'Auto',
        initialySelectedRes: 0,
        customSourcePicker: undefined
      }, settings, label);
      videojs.dom.addClass(menuButton.el(), 'vjs-resolution-button');
      const addedButton = player.controlBar.addChild(menuButton, {});
      const barEl = player.controlBar.el();
      const buttonEl = addedButton.el();
      const downloadEl = barEl.querySelector('.vjs-vjsdownload');
      if (downloadEl && downloadEl.parentNode === barEl) {
        const afterDownload = (downloadEl as any).nextSibling;
        if (afterDownload) {
          barEl.insertBefore(buttonEl, afterDownload);
        } else {
          barEl.appendChild(buttonEl);
        }
      } else {
        const pip = player.controlBar.getChild?.('PictureInPictureToggle');
        if (pip && (pip as any).el) {
          barEl.insertBefore(buttonEl, (pip as any).el());
        } else {
          barEl.appendChild(buttonEl);
        }
      }
      player.controlBar.resolutionSwitcher = addedButton;
    }

    if (player.techName_ === 'Youtube') {
      initResolutionForYt(player, settings, label);
    }
  });
}

// Register the plugin
const registerPlugin = videojs.registerPlugin || videojs.plugin;
registerPlugin('videoJsResolutionSwitcher', videoJsResolutionSwitcher);

export default videoJsResolutionSwitcher;


